// Shelves › Reading (Phase 6.2 Part 1, agreed with Kristi Oct 28): the shelves, their order
// and starting views, and the plain rules the page uses. No database calls here, so the rules
// can be tested on their own (tests/logic/shelves.test.ts).

import { fmtNum } from '../ui/ListFoot';
import type { ReadType } from '../ui/bits';
import type { Status } from '../book/types';
import { addDays, daysBetween, dayText, fuzzyText, monthHeading, monthText, todayIn } from '../book/model';
import type { Copy, Edition, Loan, Read } from '../book/types';
import { loanState, needsYou, stateWords } from '../lib/libraryStates';

export type ShelfKey = 'currently_reading' | 'up_next' | 'future_reads' | 'finished' | 'not_for_me' | 'no_status';

export interface ShelfDef {
  key: ShelfKey;
  name: string;
  description: string;
  /** Starting view: cards or covers (the quiet links change it, remembered on this device). */
  show: 'cards' | 'covers';
  /** Starts open (each fold is remembered on this device). */
  open: boolean;
  /** What "One order" is called on this shelf. */
  oneOrder: string;
  /** How much shows at first: two rows of covers, or this many cards. */
  firstCards?: { wide: number; narrow: number };
}

/** Shelf order on the Reading tab (decisions doc › Shelves rethink; No shelf yet at the bottom, Kristi Oct 28). */
export const SHELVES: ShelfDef[] = [
  { key: 'currently_reading', name: 'Currently Reading', description: 'What you’re reading now, and what you’re reading but have paused.', show: 'cards', open: true, oneOrder: 'Reading order' },
  { key: 'up_next', name: 'Up Next', description: 'The short list: the books you’re most likely to pick up for your next read.', show: 'cards', open: true, oneOrder: 'Reading order', firstCards: { wide: 12, narrow: 6 } },
  { key: 'future_reads', name: 'Future reads', description: 'Books you’d like to read someday, and books you’ll browse from to add to Up Next.', show: 'covers', open: true, oneOrder: 'Reading order' },
  { key: 'finished', name: 'Finished', description: 'Every book you’ve read, most recent first.', show: 'covers', open: true, oneOrder: 'Most recent first' },
  { key: 'not_for_me', name: 'Not for me', description: 'Books you’ve stopped reading for good, and books you’ve decided to pass on.', show: 'covers', open: false, oneOrder: 'Newest first' },
  // Folded to start (filters review, Kristi Nov 2): its to-do home is My books › Update details.
  { key: 'no_status', name: 'No shelf yet', description: 'Books not on a shelf yet, newest added first. Move each one where it belongs.', show: 'covers', open: false, oneOrder: 'Newest first' },
];

export const shelfDef = (k: ShelfKey) => SHELVES.find((s) => s.key === k)!;

/** One book as a shelf shows it. */
export interface ShelfBook {
  id: string;
  label: number;
  workId: string;
  status: Status | null;
  title: string;
  author: string;
  /** The first author's people ID, for the link to their page (Discover, Part 2). */
  authorId?: string;
  series: string | null;
  /** The series' ID, for the link to its page. */
  seriesId?: string | null;
  cover: string | null;
  readType: ReadType | null;
  /** Your read type's own name (a custom one has no colour key). */
  readTypeName?: string | null;
  literary?: string | null;
  favorite?: boolean;
  genre: string | null;
  /** The first subgenre you listed. */
  subgenre?: string | null;
  interest: number | null;
  goodreads: number | null;
  /** Up Next / Future reads: place in your order. */
  position: number | null;
  /** When it arrived on its shelf (for "on Up Next 3 months"). */
  onShelfSince: string | null;
  addedAt: string;
  /** How it joined your list (books.added_how). */
  addedHow?: 'by_hand' | 'discover' | 'import' | 'other';
  /** When Review last saved it (0042); empty until reviewed. */
  lastReviewedAt?: string | null;
  /** Not from the library (0045): My books › Holds to place stops suggesting it. */
  notFromLibrary?: boolean;
  /** The import batch that added it. */
  importBatch?: string | null;
  /** A copy of your own you have now; undefined until the details are read. */
  owned?: boolean;
  // Reading (cards on Currently Reading).
  reading?: {
    startedOn: string | null;
    percent: number | null;
    pausedOn: string | null;
    /** Leave paused for now (0036): ask again from this day. */
    checkInAfter?: string | null;
    formats: string[];
  };
  readAgain?: boolean;
  tryAgain?: boolean;
  /** When ↻ and ↺ were set (Revisit's "newest" when no read gives a date). */
  readAgainAt?: string | null;
  tryAgainAt?: string | null;
  /** Cards: the copies, editions and reads Open in needs (the book page's openTargets). */
  openIn?: { copies: Copy[]; editions: Edition[]; reads: Read[] };
  /** Cards: the latest finished read. */
  lastFinished?: { on: string | null; precision: string | null } | null;
  /** Cards: stopped reads since the last finish. */
  tries?: { count: number; lastStoppedAt: number | null } | null;
  loan?: Loan;
  /** The loan's copy ("Libby audio"), when the book has more than one library copy with a loan (Kristi, Oct 29). */
  loanCopy?: string;
  note: string | null;
  // Finished: the latest finished read.
  finished?: { on: string | null; precision: string | null; words: string | null };
  // Not for me.
  notForMe?: { kind: 'abandoned' | 'passed_on' | null; on: string | null; chips?: string[] };
}

/**
 * Why a book is Not for me, on its card (Kristi, Oct 28): the quick reasons, then your own words
 * (a pass's reason, or an abandoned read's stop reason); nothing when neither was given.
 */
export function notForMeWhy(chips: string[], words: string | null): string | null {
  const parts = [...new Set(chips.filter(Boolean))];
  if (words?.trim()) parts.push(words.trim());
  return parts.length ? parts.join(' · ') : null;
}

/** A read type name of yours as the four standard keys (other names show no colour bar). */
export function readTypeKey(name: string | null | undefined): ReadType | null {
  const k = (name ?? '').trim().toLowerCase();
  return k === 'light' || k === 'absorbing' || k === 'demanding' || k === 'study' ? k : null;
}

/** How many covers fit in one row of a cover shelf, from its inner width (tiles 104 px, 92 on a phone, gap 18). */
export function coversPerRow(innerWidth: number, narrow: boolean): number {
  const tile = narrow ? 92 : 104;
  const gap = 18;
  return Math.max(1, Math.floor((innerWidth + gap) / (tile + gap)));
}

/** How many cards fit in a row (the card grid: at least 330 px each, gap 16; one on a phone). */
export function cardsPerRow(width: number, narrow: boolean): number {
  return narrow ? 1 : Math.max(1, Math.floor((width + 16) / (330 + 16)));
}

export const num = fmtNum;

/** The count beside a shelf's name. */
export function countText(key: ShelfKey, total: number, paused = 0): string {
  if (key === 'currently_reading') return paused ? `${total - paused} reading · ${paused} paused` : `${total} reading`;
  return total === 1 ? '1 book' : `${num(total)} books`;
}

/** Currently Reading: the ones you're reading first (your order, else newest started), paused ones at the foot. */
export function readingOrder(books: ShelfBook[]): { reading: ShelfBook[]; paused: ShelfBook[] } {
  const by = (a: ShelfBook, b: ShelfBook) =>
    (a.position ?? Infinity) - (b.position ?? Infinity) || (b.reading?.startedOn ?? '').localeCompare(a.reading?.startedOn ?? '') || a.label - b.label;
  const sorted = [...books].sort(by);
  return { reading: sorted.filter((b) => !b.reading?.pausedOn), paused: sorted.filter((b) => b.reading?.pausedOn) };
}

/** Groups for "By read type": the four in order, then No read type; empty groups left out. */
export function byReadType<T extends { readType: ReadType | null }>(books: T[]): { label: string; key: ReadType | 'none'; books: T[] }[] {
  const keys: (ReadType | 'none')[] = ['light', 'absorbing', 'demanding', 'study', 'none'];
  const names: Record<ReadType | 'none', string> = { light: 'Light', absorbing: 'Absorbing', demanding: 'Demanding', study: 'Study', none: 'No read type' };
  return keys
    .map((k) => ({ key: k, label: names[k], books: books.filter((b) => (b.readType ?? 'none') === k) }))
    .filter((g) => g.books.length);
}

/** Finished's month heading: "October 2026" (R8 W3: full names in headings); a year-only read "2019"; undated "Date not recorded". */
export function finishedGroup(f: ShelfBook['finished']): string {
  if (!f?.on) return f?.words ? f.words : 'Date not recorded';
  if (f.precision === 'year') return String(Number(f.on.slice(0, 4)));
  if (f.precision === 'about') return `About ${Number(f.on.slice(0, 4))}`;
  return monthHeading(f.on);
}

/** The day a timestamp fell on in Pacific time ("Added" dates, R8 W3); a plain date stays as it is. */
export const pacificDay = (ts: string) => (ts.length <= 10 ? ts : todayIn('America/Los_Angeles', new Date(ts)));

/** Finished, newest first: dated by their finish (undated last), each book once (its latest finished read). */
export function finishedOrder(books: ShelfBook[]): ShelfBook[] {
  return [...books].sort((a, b) => {
    const x = a.finished?.on ?? '';
    const y = b.finished?.on ?? '';
    if (x && y) return y.localeCompare(x) || b.label - a.label;
    if (x || y) return x ? -1 : 1;
    return b.label - a.label;
  });
}

/**
 * A card's library pill (Kristi, Oct 28): one short line. The card's left bar already says Library
 * copy, so no "Library ·"; places in line and copies stay on the book page. The words are the
 * library state's (R2, L5): "Loan ended Oct 28", "Ready · borrow by Nov 3", "Due Nov 4", "Due
 * today", "Check Libby · expected Nov 6", "Not started · due Nov 19", "On hold · expected Jan 13, 2027".
 */
export function cardLoanPill(loan: Loan, today: string, reading = false): string | null {
  const state = loanState(asLoanLike(loan), reading, today);
  if (!state) return null;
  const borrowBy = loan.borrow_by ?? (loan.ready_on ? addDays(loan.ready_on, 3) : null);
  return stateWords(state, { dueOn: loan.due_on, borrowBy, expectedOn: loan.expected_on }, today);
}

/** A book page / card loan as the library states read it. */
export const asLoanLike = (l: Loan) => ({ step: l.step, dueOn: l.due_on, expectedOn: l.expected_on, checkDays: l.check_days, quietUntil: l.quiet_until ?? null });

/** The card's library pill, naming the copy when the book has more than one library copy with a loan. */
export function loanPillOf(b: Pick<ShelfBook, 'loan' | 'loanCopy'> & { status?: ShelfBook['status'] }, today: string, reading = b.status === 'currently_reading'): string | null {
  const p = b.loan ? cardLoanPill(b.loan, today, reading) : null;
  return p && b.loanCopy ? `${b.loanCopy} · ${p}` : p;
}

/** The pills on a Currently Reading card: the library loan (any active step), then %. */
export function readingPills(b: ShelfBook, today: string): string[] {
  const out: string[] = [];
  const lib = loanPillOf(b, today, true);
  if (lib) out.push(lib);
  if (b.reading?.percent !== null && b.reading?.percent !== undefined) out.push(`${Math.round(b.reading.percent)}%`);
  return out;
}

/** The quiet line on a Currently Reading card: "Kindle · Audible · started Oct 6 · day 15", or when paused. */
export function readingLine(b: ShelfBook, today: string): string {
  const r = b.reading;
  if (!r) return '';
  const parts = [...r.formats];
  if (r.pausedOn) {
    const d = daysBetween(r.pausedOn, today);
    parts.push(`paused ${dayText(r.pausedOn, today)}${d >= 14 ? ` · ${Math.floor(d / 7)} weeks ago` : ''}`);
  } else if (r.startedOn) {
    parts.push(`started ${dayText(r.startedOn, today)} · day ${daysBetween(r.startedOn, today) + 1}`);
  }
  return parts.join(' · ');
}

/**
 * A card has the Library copy bar exactly when it has a library pill (Kristi, Oct 28): an active
 * hold or loan, on any shelf, even when you also own a copy.
 */
export const cardLibrary = (b: ShelfBook, today: string) => Boolean(b.loan && cardLoanPill(b.loan, today));

/** The copy pill on an Up Next card: a library step, else Owned; not owned shows the dashed pill instead (Kristi, Oct 28). */
export function copyPill(b: ShelfBook, today: string): string | null {
  const lib = loanPillOf(b, today);
  if (lib) return lib;
  return b.owned ? 'Owned' : null;
}

/** Genre and subgenre under a card's cover: "Mystery · Police procedural" (it may wrap). */
export const genreLine = (b: Pick<ShelfBook, 'genre' | 'subgenre'>) => [b.genre, b.subgenre].filter(Boolean).join(' · ') || null;

/** Up Next card's quiet line: "#3 · Interest 4 · ★ 4.08 · on Up Next 5 months". */
export function upNextLine(b: ShelfBook, rank: number, today: string, shelf = 'Up Next'): string {
  // Interest isn't repeated here: the card's dots show it (Kristi, Oct 28).
  const parts = [`#${rank}`];
  if (b.goodreads) parts.push(`★ ${b.goodreads.toFixed(2)}`);
  if (b.onShelfSince) parts.push(`on ${shelf} ${howLong(b.onShelfSince.slice(0, 10), today)}`);
  return parts.join(' · ');
}

/** "3 days", "5 weeks", "4 months", "2 years". */
export function howLong(from: string, today: string): string {
  const d = Math.max(0, daysBetween(from, today));
  if (d < 14) return d === 1 ? '1 day' : `${d} days`;
  if (d < 61) return `${Math.floor(d / 7)} weeks`;
  if (d < 730) return `${Math.floor(d / 30.4)} months`;
  return `${Math.floor(d / 365.25)} years`;
}

/** A cover tile's quiet line, per shelf. */
export function tileLine(key: ShelfKey, b: ShelfBook, today: string): string | undefined {
  if (key === 'not_for_me') return b.notForMe?.kind === 'abandoned' ? 'Abandoned' : b.notForMe?.kind === 'passed_on' ? 'Passed on' : undefined;
  if (key === 'currently_reading' && b.reading) return b.reading.pausedOn ? `Paused ${dayText(b.reading.pausedOn, today)}` : b.reading.percent !== null ? `${Math.round(b.reading.percent)}%` : undefined;
  if (key === 'no_status') return `Added ${dayText(pacificDay(b.addedAt), today)}`;
  return undefined;
}

/** A short library pill on a cover (covers keep the pill, not the buttons or dates). */
export function tilePill(b: ShelfBook, today: string): string | undefined {
  const l = b.loan;
  if (!l) return undefined;
  if (l.step === 'borrowed') return l.due_on ? `Due ${Math.max(0, daysBetween(today, l.due_on))}d` : 'Borrowed';
  if (l.step === 'ready') return 'Ready';
  if (l.step === 'on_hold') return 'Hold';
  return undefined;
}

/** "Finished Mar 2024" on a card (Kristi, Oct 28): the latest finish, by its precision, through the one date format. */
export function finishedLine(f: { on: string | null; precision: string | null; words?: string | null } | null | undefined): string | null {
  if (!f) return null;
  if (f.words?.trim() && !f.on) return `Finished ${f.words.trim()}`;
  if (!f.on) return 'Finished, date not recorded';
  if (f.precision === 'year' || f.precision === 'about') return `Finished ${fuzzyText(f.on, f.precision)}`;
  return `Finished ${monthText(f.on)}`;
}

/** The marks as their own pills, apart from the finished line: ↻ Read again; ↺ Try again with its tries. */
export function markPills(b: ShelfBook): string[] {
  const out: string[] = [];
  if (b.readAgain) out.push('↻ Read again');
  if (b.tryAgain) {
    const t = b.tries;
    out.push(['↺ Try again', t && t.count > 1 ? `${t.count} tries` : null, t?.lastStoppedAt !== null && t?.lastStoppedAt !== undefined ? `stopped at ${Math.round(t.lastStoppedAt)}%` : null].filter(Boolean).join(' · '));
  }
  return out;
}

/** The paused check-in (decisions doc, Oct 22; Kristi, Oct 28): due 4 weeks after the pause, or from "ask again" on. */
export function pauseCheckIn(r: ShelfBook['reading'], today: string): { weeks: number } | null {
  if (!r?.pausedOn) return null;
  const from = r.checkInAfter ?? addDays(r.pausedOn, 28);
  if (today < from) return null;
  return { weeks: Math.max(4, Math.floor(daysBetween(r.pausedOn, today) / 7)) };
}

/**
 * A library step a card can act on (Kristi, Oct 28; Oct 29 with the alert line): a loan or hold in
 * one of the five states that need you (R2, L1): loan ended, ready, due soon, check Libby (your
 * library's window), not started.
 */
export function libraryNeedsYou(loan: Loan | undefined, reading: boolean, today: string): boolean {
  return Boolean(loan && needsYou(loanState(asLoanLike(loan), reading, today)));
}
